import * as React from 'react';

import { useQuery } from '@apollo/client';
import { DescriptionPage_LearningProductType as ProductVariant } from '__generated__/graphql-types';
import type { ApolloError } from 'apollo-client';

import type { Recommender } from '@coursera/event-pulse-types';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useConvertSpecializationPageToCollectionEntity } from 'bundles/unified-description-page-common/hooks/useConvertSpecializationPageToCollectionEntity';
import { useConvertXdpSimilarProductToCollectionEntity } from 'bundles/unified-description-page-common/hooks/useConvertXdpSimilarProductToCollectionEntity';
import XdpSimilarProductsQuery from 'bundles/unified-description-page-common/queries/XdpSimilarProducts.graphql';
import type { ProductCardSimilarSpecializationFragmentFragment as ProductCardSimilarSpecializationFragment } from 'bundles/unified-description-page-common/queries/__generated__/ProductCardSimilarSpecializationFragment';
import type {
  XdpSimilarProductsQuery as XdpSimilarProductsQueryType,
  XdpSimilarProductsQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/XdpSimilarProducts';
import getIdToPrefixedStringKey from 'bundles/unified-description-page-common/utils/getIdToPrefixedStringKey';

export type XDPPhase2Treatment = 'control' | 'B' | 'C';

export type XDPRedesignPhase2ContextType = {
  treatment: XDPPhase2Treatment;
  testVariantActive: boolean;
  comparisonTableData: CollectionEntity[];
  recommendationCollectionData: CollectionEntity[];
  pureLoading: boolean;
  rankedLoading: boolean;
  pureError?: ApolloError;
  rankedError?: ApolloError;
  pureRecommender?: Recommender;
  rankedRecommender?: Recommender;
  enableXDPRedesignKIM: boolean;
  enableXDPRedesignHero: boolean;
  enableXDPRedesignDescriptionRework: boolean;
  enableXDPRedesignClickableSkills: boolean;
};

const XDPRedesignPhase2Context = React.createContext<XDPRedesignPhase2ContextType>({
  treatment: 'control',
  testVariantActive: false,
  comparisonTableData: [],
  recommendationCollectionData: [],
  pureLoading: false,
  rankedLoading: false,
  pureError: undefined,
  rankedError: undefined,
  pureRecommender: undefined,
  rankedRecommender: undefined,
  enableXDPRedesignKIM: false,
  enableXDPRedesignHero: false,
  enableXDPRedesignDescriptionRework: false,
  enableXDPRedesignClickableSkills: false,
});

export const useXDPRedesignPhase2Data = () => {
  return React.useContext(XDPRedesignPhase2Context);
};

const XDPRedesignPhase2Provider = ({ children }: React.PropsWithChildren) => {
  const specializationPageData = useSpecializationPageData();
  const convertXdpSimilarProductToCollectionEntity = useConvertXdpSimilarProductToCollectionEntity();
  const convertSpecializationPageToCollectionEntity = useConvertSpecializationPageToCollectionEntity();
  const treatment = GrowthDiscoveryEpicClient.get('enableXDPRedesignPhase2');
  const testVariantActive = treatment !== 'control';
  const enableXDPRedesignKIM = treatment === 'C';
  const enableXDPRedesignHero = treatment === 'C';
  const enableXDPRedesignDescriptionRework = treatment === 'C';
  const enableXDPRedesignClickableSkills = treatment === 'C';

  const getQueryVariables = (rankedTreatment?: boolean): XdpSimilarProductsQueryVariables => ({
    // Use the appropriate recommender config for the treatment
    queryCollectionId: rankedTreatment ? 'xdp-similar-s12n-ranked' : 'xdp-similar-s12n',
    additionalInput: {
      entity_prefixed_stringkey: getIdToPrefixedStringKey(
        specializationPageData?.id ?? '',
        ProductVariant.Specialization
      ),
      // Recs queries require difficulty to be of the format "Beginner", "Intermediate", "Advanced"
      difficulty_level: (specializationPageData?.difficultyLevel ?? 'Beginner')
        .toLowerCase()
        .replace(/\b\w/g, (firstLetter) => firstLetter.toUpperCase()),
      primary_languages: specializationPageData?.primaryLanguages ?? ['en'],
    },
  });

  // Pure similarity (data for ComparisonTableModule)
  const {
    data: pureData,
    loading: pureLoading,
    error: pureError,
  } = useQuery<XdpSimilarProductsQueryType, XdpSimilarProductsQueryVariables>(XdpSimilarProductsQuery, {
    variables: getQueryVariables(false),
    skip: !testVariantActive || !specializationPageData?.id,
    context: { clientName: 'gatewayGql' },
    ssr: false,
    errorPolicy: 'all',
  });

  // Ranked similarity (data for RecommendationCollectionModule)
  const {
    data: rankedData,
    loading: rankedLoading,
    error: rankedError,
  } = useQuery<XdpSimilarProductsQueryType, XdpSimilarProductsQueryVariables>(XdpSimilarProductsQuery, {
    variables: getQueryVariables(true),
    skip: !testVariantActive || !specializationPageData?.id,
    context: { clientName: 'gatewayGql' },
    ssr: false,
    errorPolicy: 'all',
  });

  const contextValue = React.useMemo(() => {
    const isProductCard = (item: unknown): item is ProductCardSimilarSpecializationFragment => {
      return (item as ProductCardSimilarSpecializationFragment).id !== undefined;
    };

    const processRecommendations = ({
      recommendations,
      startIndex = 0,
      count,
      recommenderHash,
      recommenderContext,
      removeFreeTrialBadge = false,
    }: {
      recommendations: typeof pureRecommendations;
      startIndex?: number;
      count?: number;
      recommenderHash?: string;
      recommenderContext?: Record<string, unknown>;
      removeFreeTrialBadge?: boolean;
    }): CollectionEntity[] => {
      const slice = count ? recommendations.slice(0, count) : recommendations;
      return slice
        .filter(isProductCard)
        .map((item, index) => {
          const entity = convertXdpSimilarProductToCollectionEntity(item, startIndex + index, removeFreeTrialBadge);
          return {
            ...entity,
            recommenderConfigurationHash: recommenderHash,
            context: recommenderContext,
          } as CollectionEntity;
        })
        .filter((entity): entity is NonNullable<typeof entity> => !!entity);
    };

    const pureRecommendations = pureData?.Recommendations?.queryCollection?.entities?.elements || [];
    const rankedRecommendations = rankedData?.Recommendations?.queryCollection?.entities?.elements || [];

    let comparisonTableData: CollectionEntity[] = [];
    let recommendationCollectionData: CollectionEntity[] = [];

    // Ensure the current s12n is the first entity in the comparison table for both test treatments
    if (testVariantActive && specializationPageData) {
      const currentEntity = convertSpecializationPageToCollectionEntity(specializationPageData, 0);
      if (currentEntity) {
        comparisonTableData = [currentEntity];
      }
    }

    const pureRecommenderHash = pureData?.Recommendations?.queryCollection?.recommenderConfigurationHash || undefined;
    const pureRecommenderContext = pureData?.Recommendations?.queryCollection?.context || undefined;

    const rankedRecommenderHash =
      rankedData?.Recommendations?.queryCollection?.recommenderConfigurationHash || undefined;
    const rankedRecommenderContext = rankedData?.Recommendations?.queryCollection?.context || undefined;

    // Add 3 pure similarity results for ComparisonTableModule
    if (testVariantActive && pureRecommendations.length) {
      comparisonTableData = [
        ...comparisonTableData,
        ...processRecommendations({
          recommendations: pureRecommendations,
          startIndex: 1,
          count: 3,
          recommenderHash: pureRecommenderHash,
          recommenderContext: pureRecommenderContext,
          removeFreeTrialBadge: true,
        }),
      ];
    }

    // Add all ranked similarity results for RecommendationCollectionModule
    if (testVariantActive && rankedRecommendations.length) {
      recommendationCollectionData = processRecommendations({
        recommendations: rankedRecommendations,
        startIndex: 0,
        recommenderHash: rankedRecommenderHash,
        recommenderContext: rankedRecommenderContext,
      });
    }

    // Recs-specific eventing data for ComparisonTableModule
    const pureRecommender = pureData?.Recommendations?.queryCollection?.recommenderConfigurationHash
      ? {
          context: pureData.Recommendations.queryCollection.context,
          hash: pureData.Recommendations.queryCollection.recommenderConfigurationHash,
        }
      : undefined;

    // Recs-specific eventing data for RecommendationCollectionModule
    const rankedRecommender = rankedRecommenderHash
      ? {
          context: rankedRecommenderContext,
          hash: rankedRecommenderHash,
        }
      : undefined;

    return {
      treatment,
      testVariantActive,
      comparisonTableData,
      recommendationCollectionData,
      pureLoading,
      rankedLoading,
      pureError,
      rankedError,
      pureRecommender,
      rankedRecommender,
      enableXDPRedesignKIM,
      enableXDPRedesignHero,
      enableXDPRedesignDescriptionRework,
      enableXDPRedesignClickableSkills,
    };
  }, [
    treatment,
    testVariantActive,
    pureData,
    rankedData,
    pureLoading,
    rankedLoading,
    pureError,
    rankedError,
    specializationPageData,
    convertXdpSimilarProductToCollectionEntity,
    convertSpecializationPageToCollectionEntity,
    enableXDPRedesignKIM,
    enableXDPRedesignHero,
    enableXDPRedesignDescriptionRework,
    enableXDPRedesignClickableSkills,
  ]);

  return <XDPRedesignPhase2Context.Provider value={contextValue}>{children}</XDPRedesignPhase2Context.Provider>;
};

export default XDPRedesignPhase2Provider;
